import { useForm, usePage, Link, router } from "@inertiajs/react";
import AppLayout from "@/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Trash2, LogOut, Settings, CalendarDays, MapPin } from "lucide-react";
import Avatar from "@/components/app/Avatar";
import { formatDateParts } from "@/lib/timeHelpers";

export default function TeamShow() {
  const { team, memberships, team_events = [], auth, routes } = usePage().props;
  const isOwner = auth.user && team.owner.id === auth.user.id;
  const myMembership = memberships.find((m) => m.user.id === auth.user?.id);

  function leaveTeam() {
    if (!myMembership) return;
    if (!confirm("Leave this team?")) return;
    router.delete(`${team.paths.memberships}/${myMembership.id}`);
  }

  function deleteTeam() {
    if (!confirm(`Delete "${team.name}"? This cannot be undone.`)) return;
    router.delete(team.paths.destroy);
  }

  function removeMember(membershipId) {
    router.delete(`${team.paths.memberships}/${membershipId}`);
  }

  return (
    <AppLayout>
      <div className="container py-8 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{team.name}</h1>
              {team.label && (
                <Badge variant="secondary" className="text-xs">{team.label}</Badge>
              )}
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">
              Owner: {team.owner.full_name}
            </p>
          </div>
          <div className="flex gap-2">
            {myMembership && !isOwner && (
              <Button variant="ghost" size="sm" onClick={leaveTeam} className="text-muted-foreground">
                <LogOut className="h-4 w-4" />
                Leave
              </Button>
            )}
            {isOwner && (
              <>
                <Button asChild variant="outline" size="sm">
                  <Link href={team.paths.edit}>
                    <Settings className="h-4 w-4" />
                    Edit
                  </Link>
                </Button>
                <Button variant="destructive" size="sm" onClick={deleteTeam}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Description / Notes */}
        {(team.description || team.note) && (
          <div className="rounded-lg border border-border bg-card p-5 mb-6 space-y-3">
            {team.description && (
              <p className="text-muted-foreground leading-relaxed">{team.description}</p>
            )}
            {team.note && (
              <div className="border-t border-border pt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Notes</p>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{team.note}</p>
              </div>
            )}
          </div>
        )}

        {/* Upcoming team events */}
        <TeamEventsSection events={team_events} isOwner={isOwner} routes={routes} />

        {/* Invite form (owner only) */}
        {isOwner && (
          <InviteForm team={team} />
        )}

        {/* Members list */}
        <div className="rounded-lg border border-border bg-card divide-y divide-border">
          <div className="px-5 py-3 flex items-center justify-between">
            <h2 className="font-semibold text-sm">
              Members
              <span className="text-muted-foreground font-normal ml-1.5">({memberships.length})</span>
            </h2>
          </div>
          {memberships.map((m) => (
            <div key={m.id} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Avatar name={m.user.full_name} url={m.user.avatar_url} />
                <span className="text-sm font-medium">{m.user.full_name}</span>
                {m.role === "owner" && (
                  <Badge variant="secondary" className="text-xs">Owner</Badge>
                )}
              </div>
              {isOwner && m.role !== "owner" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMember(m.id)}
                  className="text-muted-foreground hover:text-destructive h-7 w-7 p-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}

function TeamEventsSection({ events, isOwner, routes }) {
  if (events.length === 0) {
    if (!isOwner) return null;
    return (
      <div className="rounded-lg border border-border bg-card p-5 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold text-sm">Upcoming Events</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          No upcoming events —{" "}
          <Link href={routes.new_event} className="text-primary hover:underline">
            create one for this team
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card mb-4 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-semibold text-sm">Upcoming Events</h2>
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-1">
          {events.length}
        </span>
      </div>
      <ul className="divide-y divide-border">
        {events.map((event) => {
          const { month, day } = formatDateParts(event.starts_at);
          return (
            <li key={event.id}>
              <Link
                href={event.paths.show}
                className="flex items-start gap-3 px-5 py-3 hover:bg-muted/50 transition-colors group"
              >
                <div className="shrink-0 w-10 text-center">
                  <p className="text-xs font-medium text-primary uppercase">{month}</p>
                  <p className="text-lg font-bold leading-none">{day}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {event.title}
                  </p>
                  {event.location_name && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{event.location_name}</span>
                    </p>
                  )}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function InviteForm({ team }) {
  const form = useForm({ email_address: "" });

  function submit(e) {
    e.preventDefault();
    form.post(team.paths.memberships, {
      onSuccess: () => form.reset(),
    });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-5 mb-4 space-y-3">
      <h2 className="font-semibold text-sm">Invite a member</h2>
      <form onSubmit={submit} className="flex gap-2">
        <Input
          type="email"
          placeholder="teammate@example.com"
          value={form.data.email_address}
          onChange={(e) => form.setData("email_address", e.target.value)}
          className="flex-1"
        />
        <Button type="submit" size="sm" disabled={form.processing}>
          <UserPlus className="h-4 w-4" />
          Invite
        </Button>
      </form>
      {form.errors.email_address && (
        <p className="text-xs text-destructive">{form.errors.email_address}</p>
      )}
    </div>
  );
}

